/**
 * components/riskTerminal/Watchlist.jsx
 *
 * upstox-043 — the design's third track, "TRACK 3 · WATCHLIST"
 * (handover/design/extracted/markup.html; logic.js listDefs / lists / watch):
 * the WATCHLIST header, the list tabs, the rule line under them, and rows.
 *
 * - "NIFTY ±10" / "SENSEX ±10" (automatic): option-chain rows — CE price ·
 *   STRIKE · PE price, lowest strike on top, the ATM row marked. The design's
 *   per-instrument row doesn't fit a chain; this keeps its type, tones,
 *   paddings and B / S buttons (one pair per side).
 * - "FIXED": the design's rows (symbol · price · B · S), a search box in the
 *   design's field style on top, × on row hover to remove.
 * B / S stay disabled: this terminal places no orders yet.
 *
 * Prices come from the server's watchlist read (Kite ticks). LIVE in ink;
 * CLOSED (after hours, 15:40 — market_hours.MARKET_CLOSE) in the secondary
 * tone; STALE or UNAVAILABLE as "—" with the reason on hover.
 *
 * upstox-045: ONE status line under the tabs ("CLOSED · last prices at
 * 15:40", or the stale / unavailable reason) — no label beside any price.
 * The chain's strike is a column of its own: a shaded band (paper tint),
 * full row height, bold and centred; tabular prices, CE right-aligned
 * against the band, PE left-aligned from it; B / S on every row, both sides.
 */

import { OrdersNoteCtx } from './ordersNote';
import React, { useEffect, useRef, useState } from 'react';
import { C, FONT, btnGhost, iconBtnSm, icSm, disabledLook } from './theme';
import { Refusal } from './controls';
import { heldChip } from './heldChips';

const PREF_KEY = 'riskTerminal.watch.v1';
const inputStyle = { height: '32px', background: '#fff', border: '1px solid ' + C.line3, color: C.ink, fontFamily: FONT, fontSize: '13px', padding: '0 10px', outline: 'none', width: '100%' };
const bsStyle = { ...btnGhost, height: '22px', width: '20px', padding: 0, fontSize: '9px', color: C.ink4, textAlign: 'center', ...disabledLook };
// upstox-084: the column is 380 px (was 330): each side of the chain now holds a fixed 28 px slot for the held-quantity chip
// ("+700", the phone's chip, one shared function), always reserved, so a row never changes width when a position appears.
const WATCH_W = '380px';
const CHIP_W = '28px';
// upstox-053: the chain's widths at 330 px — prices sized for "12,345.67" plus a
// FIXED gap to the strike band that no number can eat into; the room comes from
// the outer padding and the B / S column (each button still a 20 × 22 target)
const BAND_W = '72px';     // the strike band
const SIDE_W = '42px';     // B + S
const PRICE_MIN = '64px';  // "12,345.67" at 13 px, tabular figures
const BAND_GAP = '8px';    // price ↔ strike band, never eaten
const OUTER_GAP = '4px';   // B / S ↔ price

function readTab() {
  try { return window.localStorage.getItem(PREF_KEY) || null; } catch (e) { return null; }
}
function writeTab(v) {
  try { window.localStorage.setItem(PREF_KEY, v); } catch (e) { /* view pref only */ }
}

function fmt(v) {
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function PriceCell({ p, align = 'right', width = '72px', style }) {
  const base = { width, textAlign: align, fontSize: '13px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...style };
  if (!p) return <div style={{ ...base, color: C.ink5 }} data-tip="not listed for this strike">—</div>;
  const live = p.state === 'LIVE';
  const closed = p.state === 'CLOSED';
  return (
    <div style={{ ...base, color: live ? C.ink : closed ? C.ink3 : C.ink5 }}
      data-tip={p.reason || (closed ? 'last price · market closed' : live ? 'live · Kite' : '')}>
      {p.price === null || p.price === undefined ? '—' : fmt(p.price)}
    </div>
  );
}

/**
 * upstox-050: a tick time as epoch ms, or null. The server sends the tick
 * store's `arrived_at` — an ISO string (desk_ticker: datetime.isoformat());
 * an epoch in seconds is accepted too. Anything unreadable is null, so the
 * screen never shows "Invalid Date".
 */
export function tickMs(at) {
  if (at === null || at === undefined || at === '') return null;
  const ms = typeof at === 'number' ? at * 1000 : Date.parse(at);
  return Number.isFinite(ms) ? ms : null;
}

/** The latest tick time among a list's prices (epoch ms), or null. */
export function lastTickAt(list) {
  if (!list) return null;
  const ps = list.kind === 'RULE' ? (list.rows || []).flatMap(r => [r.ce, r.pe]) : (list.items || []);
  let t = null;
  for (const p of ps) {
    const ms = p && p.price != null ? tickMs(p.at) : null;
    if (ms != null && (t === null || ms > t)) t = ms;
  }
  return t;
}

const istDay = ms => new Date(ms).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

/** "15:39" today; "Thu 1 Oct 15:39" on another day (IST). */
export function tickWhen(ms, nowMs) {
  const hm = new Date(ms).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
  if (nowMs != null && istDay(ms) === istDay(nowMs)) return 'at ' + hm;
  const d = new Date(ms);
  const wd = d.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'Asia/Kolkata' });
  const dm = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
  return wd + ' ' + dm + ' ' + hm;
}

/** The one status line: closed with the last tick's time, or the feed's fault. */
export function statusLine(watch, list) {
  // upstox-074 (item 15): only a feed fault replaces the header line; "closed · last prices" is now the
  // header's own "last <time>"
  if (watch && watch.note) return { text: watch.note, tone: 'fault' };
  return null;
}

/** "04:09" (IST) of a tick time in epoch ms. */
function hhmmIst(ms) {
  return new Date(ms).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
}

/**
 * The one header line under the list tabs (upstox-074, item 15): an automatic list reads
 * "<13 Oct> expiry · ATM ±N strikes", a fixed list "Fixed list · N instruments"; "last <time>" (the newest
 * tick) sits on the right. Re-centre and spot are not in the header (they stay in the tab's hover card).
 */
export function headerLine(list) {
  if (!list) return null;
  const t = lastTickAt(list);
  const last = t != null ? 'last ' + hhmmIst(t) : '';
  if (list.kind === 'RULE') {
    const r = list.rule || {};
    const each = r.strikes_each_side || r.width || 10;
    const exp = list.expiry ? new Date(list.expiry + 'T00:00:00+05:30').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' }) : null;
    return { left: (exp ? exp + ' expiry' : 'no expiry listed') + ' · ATM ±' + each + ' strikes', last };
  }
  const n = (list.items || []).length;
  return { left: 'Fixed list · ' + n + ' instrument' + (n === 1 ? '' : 's'), last };
}

function BS({ side }) {
  const note = React.useContext(OrdersNoteCtx);                    // upstox-074: the account's note, not a fixed sentence
  const t = note + (side ? ' · ' + side : '');
  return (
    <>
      <button type="button" disabled data-tip={t} aria-label={'Buy' + (side ? ' ' + side : '')} style={bsStyle}>B</button>
      <button type="button" disabled data-tip={t} aria-label={'Sell' + (side ? ' ' + side : '')} style={bsStyle}>S</button>
    </>
  );
}

function ruleLine(l) {
  if (!l || l.kind !== 'RULE') return '';
  if (l.reason) return l.reason;
  const exp = l.expiry ? new Date(l.expiry + 'T00:00:00+05:30').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' }).toUpperCase() : '—';
  const at = l.centred_at ? new Date(l.centred_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' }) : null;
  const sp = l.centred_spot != null ? Number(l.centred_spot).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : null;
  // upstox-051: the list's own rule — "ATM ±15 · next 13 OCT · re-centre at 2 · re-centred 11:42 on 24,918.2"
  const r = l.rule || {};
  const each = r.strikes_each_side || r.width || 10;
  const sides = r.sides === 'CE' ? ' · calls' : r.sides === 'PE' ? ' · puts' : '';
  // upstox-053: no live spot (the server restarted outside market hours) → said, never hidden
  const notLive = l.spot_live === false && l.spot_source === 'stored' ? ' · spot not live (last centred spot)' : '';
  return 'ATM ±' + each + sides + ' · ' + (r.expiry || 'nearest') + ' ' + exp + ' · re-centre at ' + (r.recentre_strikes || 3)
    + (at ? ' · re-centred ' + at + (sp ? ' on ' + sp : '') : ' · waiting for the first price') + notLive;
}

export default function Watchlist({ watch, error, api, onChanged, initial, onCollapse, held }) {
  const lists = (watch && watch.lists) || [];
  const [tab, setTab] = useState(() => (initial && initial.tab) || readTab());
  const active = lists.find(l => l.name === tab) || lists[0] || null;
  useEffect(() => { if (active) writeTab(active.name); }, [active && active.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabStyle = on => ({ ...btnGhost, height: '25px', color: on ? '#fff' : C.ink3, background: on ? C.ink : '#fff', borderColor: on ? C.ink : C.line3, fontWeight: on ? 600 : 400 });

  return (
    <div style={{ flex: '0 0 ' + WATCH_W, width: WATCH_W, minWidth: 0, maxWidth: WATCH_W, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: '#fff' }}>
        <div style={{ fontSize: '11px', letterSpacing: '0.14em', color: C.ink, fontWeight: 600 }}>WATCHLIST</div>
        {onCollapse && <div style={{ flex: 1 }} />}
        {onCollapse && (
          <button type="button" className="rt-ib" data-collapse="watch" onClick={onCollapse} data-tip="Collapse the watchlist to a strip"
            style={{ ...iconBtnSm, flex: '0 0 26px' }}><span style={icSm}>keyboard_double_arrow_right</span></button>
        )}
      </div>
      <div style={{ display: 'flex', gap: '6px', padding: '12px 16px 4px 16px', flexWrap: 'wrap' }}>
        {lists.map(l => (
          <button key={l.id} type="button" style={tabStyle(active && active.id === l.id)} onClick={() => setTab(l.name)}
            data-tip={l.kind === 'RULE' ? ruleLine(l) : 'Instruments you pick · at most ' + (l.max || 50)}>{l.name}</button>
        ))}
      </div>
      {(() => {
        const st = statusLine(watch, active);
        const h = headerLine(active);
        if (st) return <div data-watch-status={st.tone} style={{ margin: '0 16px 8px 16px', fontSize: '10px', letterSpacing: '0.1em', fontWeight: 700, color: C.red }}>{st.text}</div>;
        return h && (
          <div data-watch-header style={{ display: 'flex', alignItems: 'baseline', gap: '10px', padding: '2px 16px 8px 16px', fontSize: '11px', color: C.ink3, letterSpacing: '0.02em', minHeight: '14px' }}>
            <span data-watch-header-left>{h.left}</span><span style={{ flex: 1 }} />{h.last && <span data-watch-last style={{ color: C.ink4, fontVariantNumeric: 'tabular-nums' }}>{h.last}</span>}
          </div>
        );
      })()}
      {error && !watch && <Refusal text={'Watchlist unavailable · ' + error} style={{ margin: '0 16px 8px 16px' }} />}
      {active && active.kind === 'RULE' && <Chain list={active} held={held} />}
      {active && active.kind === 'FIXED' && <Fixed list={active} api={api} onChanged={onChanged} initial={initial} />}
    </div>
  );
}

function HeldChip({ text, side }) {
  return (
    <span style={{ flex: '0 0 ' + CHIP_W, width: CHIP_W, display: 'flex', justifyContent: side === 'CE' ? 'flex-start' : 'flex-end', alignItems: 'center' }}>
      {text && <span data-held data-held-side={side} data-tip={'You hold ' + text + ' of this contract'} style={{ fontSize: '9px', letterSpacing: '0.04em', fontWeight: 700, color: C.ink3, border: '1px solid ' + C.line4, padding: '0 2px', lineHeight: '14px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{text}</span>}
    </span>
  );
}

function Chain({ list, held }) {
  // upstox-051: calls only → CE · STRIKE; puts only → STRIKE · PE (B / S on the side shown)
  const sides = (list.rule && list.rule.sides) || 'both';
  const showCE = sides !== 'PE', showPE = sides !== 'CE';
  const col = { display: 'flex', alignItems: 'center', gap: '2px', flex: '0 0 ' + SIDE_W, width: SIDE_W };
  const priceCE = { flex: 1, minWidth: PRICE_MIN, margin: '0 ' + BAND_GAP + ' 0 ' + OUTER_GAP, overflow: 'hidden' };
  const pricePE = { flex: 1, minWidth: PRICE_MIN, margin: '0 ' + OUTER_GAP + ' 0 ' + BAND_GAP, overflow: 'hidden' };
  const band = atm => ({ flex: '0 0 ' + BAND_W, width: BAND_W, alignSelf: 'stretch', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: atm ? C.paper : C.paper3, borderLeft: '1px solid ' + C.line2, borderRight: '1px solid ' + C.line2 });
  return (
    <div style={{ overflow: 'auto', minHeight: 0, flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'stretch', padding: '0 4px', fontSize: '9px', color: C.ink4, letterSpacing: '0.1em' }}>
        {showCE && <div style={col} />}
        {showCE ? <div style={{ ...priceCE, textAlign: 'right', padding: '5px 0' }}>CE</div> : <div style={{ flex: 1 }} />}
        <div style={{ ...band(false), padding: '5px 0' }}>STRIKE</div>
        {showPE ? <div style={{ ...pricePE, textAlign: 'left', padding: '5px 0' }}>PE</div> : <div style={{ flex: 1 }} />}
        {showPE && <div style={col} />}
      </div>
      {list.rows.map(r => (
        <div key={r.strike} data-strike={r.strike} style={{ display: 'flex', alignItems: 'center', height: '32px', padding: '0 4px', background: r.atm ? C.paper2 : 'transparent', boxShadow: r.atm ? 'inset 3px 0 0 ' + C.ink : 'none' }}>
          {showCE && <div style={col}><BS side="CE" /></div>}
          {showCE ? (
            <div style={{ ...priceCE, display: 'flex', alignItems: 'center', minWidth: 'calc(' + PRICE_MIN + ' + ' + CHIP_W + ')' }}>
              <HeldChip side="CE" text={heldChip(held, list, r.strike, 'CE')} />
              <PriceCell p={r.ce} width="auto" style={{ flex: 1, minWidth: PRICE_MIN, overflow: 'hidden' }} />
            </div>
          ) : <div style={{ flex: 1 }} />}
          <div style={band(r.atm)} data-tip={r.atm ? 'at the money' : ''}>
            <div style={{ fontSize: '13px', fontVariantNumeric: 'tabular-nums', color: C.ink, fontWeight: 700, lineHeight: 1.1 }}>{Number(r.strike).toLocaleString('en-IN')}</div>
            {r.atm && <div style={{ fontSize: '8px', letterSpacing: '0.14em', color: C.ink3, fontWeight: 700, lineHeight: 1 }}>ATM</div>}
          </div>
          {showPE ? (
            <div style={{ ...pricePE, display: 'flex', alignItems: 'center', minWidth: 'calc(' + PRICE_MIN + ' + ' + CHIP_W + ')' }}>
              <PriceCell p={r.pe} align="left" width="auto" style={{ flex: 1, minWidth: PRICE_MIN, overflow: 'hidden' }} />
              <HeldChip side="PE" text={heldChip(held, list, r.strike, 'PE')} />
            </div>
          ) : <div style={{ flex: 1 }} />}
          {showPE && <div style={{ ...col, justifyContent: 'flex-end' }}><BS side="PE" /></div>}
        </div>
      ))}
      {!list.rows.length && <div style={{ padding: '12px 16px', fontSize: '11px', color: C.ink4 }}>{list.reason || 'Waiting for the first ' + ((list.rule && list.rule.underlying) || '') + ' price.'}</div>}
    </div>
  );
}

function Fixed({ list, api, onChanged, initial }) {
  const [q, setQ] = useState((initial && initial.q) || '');
  const [results, setResults] = useState((initial && initial.results) || null);
  const [note, setNote] = useState((initial && initial.note) || null);
  const [err, setErr] = useState(null);
  const [hov, setHov] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    if (initial && initial.results) return undefined;
    const term = q.trim();
    if (term.length < 2) { setResults(null); return undefined; }
    const my = ++seq.current;
    const t = setTimeout(async () => {
      const r = await api.watchSearch(term);
      if (my !== seq.current) return;
      if (r.ok) { setResults((r.data && r.data.results) || []); setErr(null); } else setErr(r.message);
    }, 200);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async res => {
    setNote(null); setErr(null);
    const r = await api.watchAdd(list.id, res.instrument_key);
    if (r.ok) { setQ(''); setResults(null); setNote(res.name + ' added.'); onChanged && onChanged(); } else setErr(r.message);
  };
  const remove = async it => {
    setNote(null); setErr(null);
    const r = await api.watchRemove(list.id, it.id);
    if (r.ok) onChanged && onChanged(); else setErr(r.message);
  };

  return (
    <div style={{ overflow: 'auto', minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '0 16px 8px 16px', position: 'relative' }}>
        <input value={q} onChange={e => { setQ(e.target.value); setNote(null); }} placeholder="Search · e.g. nifty 23000 pe, reliance, vix" style={inputStyle} />
        {results && (
          <div style={{ position: 'absolute', left: '16px', right: '16px', top: '34px', zIndex: 20, background: '#fff', border: '1px solid ' + C.line, boxShadow: '0 4px 12px rgba(26,25,23,0.08)', maxHeight: '320px', overflow: 'auto' }}>
            {!results.length && <div style={{ padding: '8px 10px', fontSize: '11px', color: C.ink4 }}>No match.</div>}
            {results.map(r => (
              <button key={r.instrument_key} type="button" onClick={() => add(r)}
                style={{ display: 'flex', width: '100%', alignItems: 'baseline', gap: '8px', padding: '7px 10px', background: '#fff', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: FONT }}>
                <span style={{ fontSize: '12px', color: C.ink, flex: 1 }}>{r.name}</span>
                <span style={{ fontSize: '9px', color: C.ink4, letterSpacing: '0.08em' }}>{r.exchange} · {r.type}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {note && <div style={{ margin: '0 16px 6px 16px', fontSize: '11px', color: C.ink3 }}>{note}</div>}
      <Refusal text={err} style={{ margin: '0 16px 6px 16px' }} />
      {!list.items.length && <div style={{ padding: '6px 16px', fontSize: '11px', color: C.ink4 }}>Empty — search above to add instruments.</div>}
      {list.items.map(it => (
        <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '10px 16px' }}
          onMouseEnter={() => setHov(it.id)} onMouseLeave={() => setHov(h => (h === it.id ? null : h))}>
          <div style={{ fontSize: '13px', color: C.ink2, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} data-tip={it.instrument_key}>{it.name}</div>
          <PriceCell p={it} />
          <BS />
          <button type="button" className="rt-ib" onClick={() => remove(it)} data-tip="Remove from the list"
            style={{ ...iconBtnSm, width: '22px', height: '22px', flex: '0 0 22px', visibility: hov === it.id ? 'visible' : 'hidden' }}>
            <span style={icSm}>close</span>
          </button>
        </div>
      ))}
    </div>
  );
}
